import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { colors } from "./colors";
import { Heading6, Body2 } from "./Text";
import ButtonWithText from "./Button";
import { useExtensionContext } from "app/popleads/modules/extension/ExtensionContext";
import useTaskComplete from "app/popleads/hooks/useTaskComplete";
import useTaskUpdate from "app/popleads/hooks/useTaskUpdate";
import moment from "moment";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { POPUP_CONTENTS } from "app/popleads/modules/extension/utils/constants";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import CustomDatePicker from "app/popleads/components/ui-kit/DateInput";

const commonTextStyles = css`
    color: ${(props) => (props.selected ? colors.neutral[500] : colors.neutral[800])};
    font-size: 12px;
`;

const flexCenter = css`
    display: flex;
    align-items: center;
`;

const CardContainer = styled.div`
    background-color: ${(props) => (props.overdue ? "#FDF5F5" : colors.white)};
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    border: 1px solid ${(props) => (props.overdue ? "#F3B4B4" : colors.neutral[100])};
    position: relative;
    transition: all 0.2s ease-in-out;
    padding-bottom: 0;
    cursor: pointer;
    max-width: 100%;
    overflow: visible;
    z-index: 1;

    &:hover {
        padding-bottom: ${(props) => (props.hideBulkActions ? "0" : "40px")};
        z-index: ${(props) => (props.hideBulkActions ? "1" : "2")};

        .action-bar {
            visibility: ${(props) => (props.hideBulkActions ? "hidden" : "visible")};
            opacity: ${(props) => (props.hideBulkActions ? "0" : "1")};
            transform: translateY(${(props) => (props.hideBulkActions ? "100%" : "0")});
        }
    }
`;

const SelectionCheckbox = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid
        ${(props) => (props.selected ? (props.greyMode ? colors.primary[900] : "#00856C") : colors.neutral[500])};
    border-radius: 4px;
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    ${flexCenter};
    justify-content: center;
    cursor: pointer;
    z-index: 1;
    background-color: ${(props) =>
        props.selected ? (props.greyMode ? colors.primary[900] : "#00856C") : "transparent"};
    transition: all 0.2s ease;

    &:after {
        content: ${(props) => (props.selected ? "'✓'" : "''")};
        color: ${colors.white};
        font-size: 12px;
        font-weight: bold;
        line-height: 1;
    }
`;

const CardContent = styled.div`
    padding: 8px;
    padding-left: ${(props) => (props.isProfileTask ? "8px" : "40px")};
    position: relative;
    width: 100%;
    box-sizing: border-box;
`;

const DetailContent = styled.div`
    max-height: ${(props) => (props.isExpanded ? "500px" : "0")};
    opacity: ${(props) => (props.isExpanded ? "1" : "0")};
    overflow: hidden;
    transition: all 0.3s ease-in-out;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: visible;
`;

const Title = styled(Heading6)`
    color: ${(props) => (props.selected ? colors.neutral[500] : colors.neutral[800])};
    font-size: 12px;
    font-weight: 500;
    ${flexCenter};
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    width: 100%;
`;

const TitleContent = styled.div`
    * {
        margin: 0;
        padding: 0;
    }
    font-size: 14px;
    color: ${colors.neutral[800]};
    font-weight: 400;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
    max-width: 100%;

    ul {
        margin: 8px 0;
        padding-left: 20px;
        list-style-type: disc;
    }

    ol {
        margin: 8px 0;
        padding-left: 20px;
        list-style-type: decimal;
    }

    li {
        margin: 4px 0;
        line-height: 1.4;
    }

    ul ul,
    ol ol,
    ul ol,
    ol ul {
        margin: 4px 0;
        padding-left: 16px;
    }

    strong {
        font-weight: 600;
    }

    em {
        font-style: italic;
    }
`;

const TypeInfo = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${colors.neutral[800]};
`;

const TypeInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;

const ImportantIcon = styled.span`
    color: ${(props) => (props.important ? colors.danger[900] : colors.neutral[300])};
    font-size: 14px;
`;

const Description = styled.div`
    color: ${(props) => (props.selected ? colors.neutral[500] : colors.neutral[800])};
    font-size: 14px;
    font-weight: 400;
    margin: 8px 0;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
    max-width: 100%;
`;

const UserInfoWrapper = styled.div`
    ${flexCenter};
    justify-content: space-between;
    margin-top: 8px;
    margin-bottom: ${(props) => (props.isExpanded ? "8px" : "0")};
`;

const UserInfo = styled.div`
    ${flexCenter};
`;

const UserAvatar = styled.img`
    width: 20px;
    height: 20px;
    border-radius: 50%;
`;

const UserName = styled(Heading6)`
    color: ${colors.neutral[900]};
    font-size: 12px;
    font-weight: 500;
    ${flexCenter};
    margin-left: 8px;
`;

const UserStatus = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
`;

const DetailItem = styled.div`
    ${commonTextStyles};
    ${flexCenter};
    font-weight: ${(props) => props.weight || 400};
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 100%;
`;

const IconDetailItem = styled.div`
    ${flexCenter};
    ${commonTextStyles};
    font-weight: 500;
    gap: 16px;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 100%;

    span {
        width: 16px;
        height: 16px;
        font-size: 16px;
        color: ${(props) => (props.selected ? colors.neutral[500] : "#5f6368")};
        flex-shrink: 0;
    }
`;

const AssignedText = styled(Body2)`
    ${commonTextStyles};
`;

const CreatedText = styled(Heading6)`
    color: ${colors.neutral[500]};
    font-size: 12px;
    font-weight: 400;
`;

const Separator = styled.hr`
    border: none;
    height: 1px;
    background-color: ${colors.neutral[200]};
    margin: 0;
`;

const ActionBar = styled.div`
    ${flexCenter};
    background-color: ${colors.neutral[50]};
    justify-content: space-around;
    gap: 8px;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    visibility: hidden;
    opacity: 0;
    transform: translateY(100%);
    transition: all 0.2s ease-in-out;
    padding: 8px 12px;
    border-top: 1px solid ${colors.neutral[100]};
    height: 40px;
    overflow: visible;
    z-index: 1000;
`;

const MoveDropdown = styled.div`
    position: absolute;
    background: ${colors.white};
    border: 1px solid ${colors.neutral[100]};
    border-radius: 6px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    padding: 8px 0;
    bottom: calc(100%);
    left: 50%;
    transform: translateX(-50%);
    display: none;
    min-width: 120px;
    z-index: 1000;

    &:after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 16px;
        height: 16px;
        background: transparent;
    }

    &:before {
        content: "";
        position: absolute;
        bottom: -6px;
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        width: 12px;
        height: 12px;
        background: ${colors.white};
        border-right: 1px solid ${colors.neutral[100]};
        border-bottom: 1px solid ${colors.neutral[100]};
    }
`;

export const MoveOption = styled.div`
    padding: 8px 16px;
    color: ${colors.neutral[800]};
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background: ${colors.neutral[50]};
    }
`;

const MoveButtonWrapper = styled.div`
    position: relative;
    padding: 4px 0;
    overflow: visible;
    z-index: 1001;

    &:hover ${MoveDropdown} {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    ${MoveDropdown}:hover {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    ${MoveDropdown} {
        display: ${(props) => (props.showDatePicker ? "none !important" : "none")};
    }
`;

const IconDetailWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    margin-bottom: 8px;
`;

const DatePickerWrapper = styled.div`
    position: absolute;
    bottom: ${({ isFirst }) => (isFirst ? "calc(100% - 165px)" : "calc(100% - 45px)")};
    left: 100%;
    transform: translateX(-45%);
    z-index: 999999;
    background: ${colors.white};
    border-radius: 8px;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
    border: 1px solid ${colors.neutral[200]};
`;
const SelectableTaskCard = ({
    task,
    setTaskToDelete,
    overdue,
    selectedTasks,
    onTaskComplete,
    hasTaskPermission,
    handleNoPermission,
    isProfileTask = false,
    index,
    greyMode = false,
    hideBulkActions = false,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const {
        modals,
        setState,
        refetchProfile,
        selectedTasksId,
        fetchTaskLists,
        selectedDate,
        fetchStaticTaskList,
    } = useExtensionContext();
    const { user } = useSelector((state) => state.auth);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedTaskForMove, setSelectedTaskForMove] = useState(null);

    useEffect(() => {
        if (selectedTasksId && selectedTasksId === task.id) {
            setIsExpanded(true);
        }
    }, [selectedTasksId, task.id]);

    const {
        id,
        metin: title,
        aciklama: description,
        zaman,
        createdTime,
        important,
        profilId: profileId,
        profil,
        taskColour: taskColor,
        assignedByName: assignedTo,
        type,
        activity,
        kayitci,
    } = task;

    const { ad, soyad, tel: phoneNumber, email, resim, accountName } = profil || {};
    const userName = profil && ad && soyad ? `${ad} ${soyad}` : "";
    const defaultImage = toAbsoluteUrl("/media/users/default.jpg");
    const [imgSrc, setImgSrc] = useState(profil && resim ? resim : defaultImage);

    const hasUserInfo = profil && ad && soyad;
    const hasDetailItems = profil && (phoneNumber || email);

    const { completeTask } = useTaskComplete();
    const { updateTask } = useTaskUpdate();

    const handleTaskCompletion = async (taskId) => {
        try {
            const response = await completeTask(taskId);
            if (response?.stat !== "ok") {
                throw new Error("Failed to complete task");
            } else {
                fetchTaskLists({ selectedDate });
                refetchProfile();
                const messagePayload = {
                    message: "XPIDER_MESSAGE_NOTIFICATION",
                    task: {
                        id: taskId,
                        status: "completed",
                    },
                };
                window.parent.postMessage(messagePayload, "*");
                toast.success("Task completed");
            }
        } catch (error) {
            toast.error("Failed to complete task");
        }
    };

    const handleCheckboxClick = (e) => {
        e.stopPropagation();
        onTaskComplete(id);
    };

    const handleCardClick = (e) => {
        if (!e.target.closest(".action-bar") && !e.target.closest(".selection-circle")) {
            setIsExpanded(!isExpanded);
        }
    };

    const handleTaskDeleteClick = () => {
        if (hasTaskPermission && handleNoPermission) {
            if (!hasTaskPermission(task)) {
                handleNoPermission();
                return;
            }
        } else if (task.customerHappiness === 1 && user?.permissions?.CUSTOMER_HAPPINESS_TASK === 0) {
            toast.error("You do not have permission to delete this task.");
            return;
        }
        setState({
            selectedModal: isProfileTask ? modals.DELETE_PROFILE_TASK : modals.DELETE_TASK,
            modalMessage:
                !!task.assignedById && user.id !== task.assignedById
                    ? "This task has been assigned to you by other user. Are you sure you want to delete it?"
                    : "Are you sure you want to delete this task?",
        });
        setTaskToDelete(id);
    };

    const handleOtherClick = (e) => {
        e.stopPropagation();
        setSelectedTaskForMove(task);
        setShowDatePicker(true);
    };

    const handleDateChange = (date) => {
        if (date) {
            // Convert date to days difference for the move function
            const selectedDate = new Date(date);
            const currentDate = new Date(zaman);
            const timeDiff = selectedDate.getTime() - currentDate.getTime();
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

            handleMove(daysDiff);
            setShowDatePicker(false);
            setSelectedTaskForMove(null);
        }
    };

    const moveOptions = [
        { label: "+7 days", value: 7 },
        { label: "+3 days", value: 3 },
        { label: "+1 day", value: 1 },
        { label: "Other", value: "other" },
    ];

    const handleMove = async (days) => {
        if (hasTaskPermission && handleNoPermission) {
            if (!hasTaskPermission(task)) {
                handleNoPermission();
                return;
            }
        }

        try {
            if (days === "other") {
                return;
            }

            await updateTask({
                taskId: id,
                taskText: title,
                currentDate: zaman,
                profileId,
                daysToAdd: days,
            });
            fetchTaskLists({ selectedDate });
            fetchStaticTaskList();
            refetchProfile();
            toast.success(`Task moved by ${days} day${days > 1 ? "s" : ""}`);
        } catch (error) {
            toast.error("Failed to move task");
        }
    };

    const actionButtons = [
        {
            variant: "success",
            onClick: (e) => {
                e.stopPropagation();
                if (hasTaskPermission && handleNoPermission) {
                    if (!hasTaskPermission(task)) {
                        handleNoPermission();
                        return;
                    }
                }
                handleTaskCompletion(id);
            },
            text: "Complete",
        },
        {
            variant: "passive",
            component: (
                <MoveButtonWrapper
                    key="move-button"
                    onClick={(e) => e.stopPropagation()}
                    showDatePicker={showDatePicker && selectedTaskForMove && selectedTaskForMove.id === id}
                >
                    <ButtonWithText variant="passive" buttonStyle="borderless" size="small">
                        Move
                    </ButtonWithText>
                    <MoveDropdown>
                        {moveOptions.map((option) => (
                            <MoveOption
                                key={option.value}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    option.value === "other" ? handleOtherClick(e) : handleMove(option.value);
                                }}
                            >
                                {option.label}
                            </MoveOption>
                        ))}
                    </MoveDropdown>
                    {showDatePicker && selectedTaskForMove && selectedTaskForMove.id === id && (
                        <DatePickerWrapper isFirst={index === 0} onClick={(e) => e.stopPropagation()}>
                            <CustomDatePicker
                                variant="move-dropdown"
                                onChange={handleDateChange}
                                onClickOutside={() => {
                                    setShowDatePicker(false);
                                    setSelectedTaskForMove(null);
                                }}
                            />
                        </DatePickerWrapper>
                    )}
                </MoveButtonWrapper>
            ),
        },
        { variant: "danger", onClick: handleTaskDeleteClick, text: "Delete" },
        {
            variant: "primary",
            component: (
                <ButtonWithText
                    key="edit-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (hasTaskPermission && handleNoPermission) {
                            if (!hasTaskPermission(task)) {
                                handleNoPermission();
                                return;
                            }
                        }

                        setState({
                            popupContent: POPUP_CONTENTS.NEW_TASK,
                            taskPage: true,
                            selectedTask: task,
                            editMode: true,
                        });
                    }}
                    variant="primary"
                    buttonStyle="borderless"
                    size="small"
                >
                    Edit
                </ButtonWithText>
            ),
        },
    ];

    const openInNewTabProfileClick = (id) => {
        window.open(`/extension?profileId=${id}`, "_blank");
    };

    return (
        <CardContainer
            overdue={overdue}
            isExpanded={isExpanded}
            hideBulkActions={hideBulkActions}
            onClick={handleCardClick}
        >
            <input
                type="checkbox"
                name="task-selection"
                checked={selectedTasks.includes(id)}
                onChange={handleCheckboxClick}
                style={{ display: "none" }}
            />
            {!isProfileTask && (
                <>
                    {!greyMode ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id={`tooltip-top`}>
                                    <span>Select Task</span>
                                </Tooltip>
                            }
                        >
                            <SelectionCheckbox
                                className="selection-circle"
                                selected={selectedTasks.includes(id)}
                                onClick={handleCheckboxClick}
                                role="checkbox"
                                aria-checked={selectedTasks.includes(id)}
                                greyMode={greyMode}
                            />
                        </OverlayTrigger>
                    ) : (
                        <SelectionCheckbox
                            className="selection-circle"
                            selected={selectedTasks.includes(id)}
                            onClick={handleCheckboxClick}
                            role="checkbox"
                            aria-checked={selectedTasks.includes(id)}
                            greyMode={greyMode}
                        />
                    )}
                </>
            )}
            <CardContent isProfileTask={isProfileTask}>
                <ContentWrapper isExpanded={isExpanded}>
                    <Title selected={selectedTasks.includes(id)}>
                        <TypeInfoWrapper>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {taskColor && <UserStatus style={{ backgroundColor: taskColor }} />}
                                <TypeInfo>{type}</TypeInfo>
                            </div>
                            <TitleContent dangerouslySetInnerHTML={{ __html: title }} />
                        </TypeInfoWrapper>

                        <div>
                            {important === 1 ? (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="tooltip-important">Important</Tooltip>}
                                >
                                    <div style={{ display: "inline-block" }}>
                                        <ImportantIcon important={true}>!</ImportantIcon>
                                        <ImportantIcon important={true}>!</ImportantIcon>
                                    </div>
                                </OverlayTrigger>
                            ) : (
                                <>
                                    <ImportantIcon important={false}>!</ImportantIcon>
                                    <ImportantIcon important={false}>!</ImportantIcon>
                                </>
                            )}
                        </div>
                    </Title>
                    {description && (
                        <>
                            <Description selected={selectedTasks.includes(id)}>{description}</Description>
                        </>
                    )}

                    {hasUserInfo && (
                        <>
                            <Separator />
                            <UserInfoWrapper isExpanded={isExpanded}>
                                <UserInfo>
                                    <UserAvatar src={imgSrc} alt={userName} onError={() => setImgSrc(defaultImage)} />
                                    <UserName>{userName}</UserName>
                                    {profileId && (
                                        <ButtonWithText
                                            iconType={"material"}
                                            variant="primary"
                                            size="small"
                                            buttonStyle="borderless"
                                            icon={"open_in_new"}
                                            onClick={() => openInNewTabProfileClick(profileId)}
                                        ></ButtonWithText>
                                    )}
                                </UserInfo>
                            </UserInfoWrapper>
                        </>
                    )}

                    <DetailContent isExpanded={isExpanded}>
                        {hasUserInfo && <Separator />}

                        {(activity || accountName) && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    marginTop: "8px",
                                }}
                            >
                                {activity && (
                                    <DetailItem
                                        selected={selectedTasks.includes(id)}
                                        weight={500}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {activity.subject}
                                        <ButtonWithText
                                            iconType={"material"}
                                            variant="primary"
                                            size="small"
                                            buttonStyle="borderless"
                                            icon={"open_in_new"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (activity.id) {
                                                    window.open(`/activities?activityId=${activity.id}`, "_blank");
                                                }
                                            }}
                                        ></ButtonWithText>
                                    </DetailItem>
                                )}
                                {accountName && (
                                    <DetailItem selected={selectedTasks.includes(id)} weight={400}>
                                        {accountName}
                                    </DetailItem>
                                )}
                            </div>
                        )}

                        {hasDetailItems && (
                            <IconDetailWrapper>
                                {profil && phoneNumber && (
                                    <IconDetailItem selected={selectedTasks.includes(id)}>
                                        <span className="material-symbols-outlined">call</span>
                                        {phoneNumber}
                                    </IconDetailItem>
                                )}

                                {profil && email && (
                                    <IconDetailItem selected={selectedTasks.includes(id)}>
                                        <span className="material-symbols-outlined">mail</span>
                                        {email}
                                    </IconDetailItem>
                                )}
                            </IconDetailWrapper>
                        )}
                        {(assignedTo || kayitci || zaman) && <Separator />}
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "8px" }}>
                            {assignedTo && <AssignedText>Assigned To {assignedTo}</AssignedText>}
                            {kayitci && <AssignedText>Created by {kayitci}</AssignedText>}
                            {zaman && (
                                <CreatedText>
                                    Created on {moment(createdTime).format("MMM DD, YYYY - hh:mm A")}
                                </CreatedText>
                            )}
                        </div>
                    </DetailContent>
                </ContentWrapper>
            </CardContent>

            <ActionBar className="action-bar">
                {actionButtons.map((button) =>
                    button.component ? (
                        button.component
                    ) : (
                        <ButtonWithText
                            key={button.text}
                            size="small"
                            variant={button.variant}
                            buttonStyle="borderless"
                            onClick={button.onClick}
                        >
                            {button.text}
                        </ButtonWithText>
                    )
                )}
            </ActionBar>
        </CardContainer>
    );
};

export default SelectableTaskCard;
